import { Fragment, ReactElement, ReactNode } from 'react';

import { styled } from '@mui/material';
import pluralize from 'pluralize';

import {
  Chip,
  HexIconElementProps,
  HexIcon,
  mergeSx,
  Placeholder as StatusMessage,
  SxProps,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';

import { Search, SearchProps } from './Search';
import { SearchProvider } from './SearchProvider';
import { SearchState } from './types';

const ResultChip = styled(Chip)(({ theme }) => {
  return [
    {
      padding: '4px 8px',
      height: 'unset',
      minHeight: 28,
      wordBreak: 'break-all' as const,
      ...theme.typography.P12M,
      color: theme.palette.common.white,
      borderRadius: '4px',
      backgroundColor: theme.palette.grey[600],
    },
  ];
});

type ChipSearchProps<T = string> = {
  title?: ReactNode;
  renderChip?: (entry: T) => ReactNode;
  entity: string;
  testId?: string;
  data?: T[];
  components?: {
    noData?: ReactNode;
    noDataIcon?: ReactElement<HexIconElementProps>;
  };
  sx?: SxProps;
  chipSx?: SxProps;
  searchProps?: SearchProps;
  hideTitle?: boolean;
};

export const ChipSearch = ({
  entity,
  title = pluralize(entity, 2),
  testId = 'data-search-box',
  data,
  renderChip,
  components: {
    noDataIcon,
    noData = (
      <StatusMessage
        icon={
          <HexIcon
            variant="notification"
            icon={noDataIcon}
            size={64}
            animationType="pulsate"
          />
        }
        titleProps={{
          color: tokens.theme.fg.structural.primaryOnInverse,
        }}
        title={`No ${pluralize(entity, 2)}`}
      />
    ),
  } = {},
  sx,
  chipSx,
  searchProps,
  hideTitle = false,
}: ChipSearchProps) => {
  return (
    <SearchProvider data={data}>
      {(ctx: SearchState) =>
        !!noData && !ctx.isLoading && !ctx.data?.length ? (
          <>{noData}</>
        ) : (
          <Stack
            gap={12}
            onClick={(e) => e.stopPropagation()}
            data-testid={testId}
            sx={sx}
          >
            {hideTitle ? null : typeof title === 'string' ? (
              <Typography
                variant="h5"
                textTransform="capitalize"
                data-testid="chip-search-title"
              >
                {title}
              </Typography>
            ) : (
              title
            )}
            <Search
              {...searchProps}
              rootSx={mergeSx(
                {
                  '&& .MuiFilledInput-root.MuiInputBase-formControl': {
                    color: tokens.theme.fg.structural.primaryOnInverse,
                    backgroundColor: 'unset',
                    border: '1px solid',
                    borderColor: 'grey.600',
                    '& .MuiInputBase-input': {
                      color: tokens.theme.fg.structural.primaryOnInverse,
                      '&::placeholder': {
                        color: tokens.theme.fg.structural.secondaryOnInverse,
                        opacity: 1,
                      },
                    },
                    '&:focus-within': {
                      '& .Ds-MagnifyingGlass': {
                        color: 'inherit',
                      },
                    },
                  },
                },
                searchProps?.rootSx
              )}
            />
            {ctx.filteredData?.length ? (
              <Typography
                variant="A1"
                color={tokens.theme.fg.structural.primaryOnInverse}
                data-testid="entity-count"
              >
                {ctx.filteredData.length}{' '}
                {pluralize(entity, ctx.filteredData.length)} found
              </Typography>
            ) : (
              <Typography
                variant="P14R"
                color={tokens.theme.fg.structural.primaryOnInverse}
              >
                No results found for{' '}
                <Typography variant="P14B" component="span">
                  “{ctx.freeText}”
                </Typography>
              </Typography>
            )}

            <Stack
              data-testid="chip-search-results"
              flexDirection="row"
              flexWrap="wrap"
              gap={12}
            >
              {ctx.filteredData?.map((record: any, index: number) => {
                const key =
                  record?.id ?? record?.key ?? `${String(record)}-${index}`;
                if (renderChip) {
                  return <Fragment key={key}>{renderChip(record)}</Fragment>;
                }
                return (
                  <ResultChip key={key} sx={chipSx}>
                    {String(record)}
                  </ResultChip>
                );
              })}
            </Stack>
          </Stack>
        )
      }
    </SearchProvider>
  );
};
